/*
 * WD_RENDER — renderElevation(state, view, opts) -> SVG string. Pure function,
 * no DOM writes, no innerHTML. Draws the wardrobe elevation for the Midnight
 * Atelier theme (brand tokens below), reproducing wardrobe-crm's drawElev()
 * proportions in mm-space per the render-engine plan.
 *
 * Depends on window.WD_GEOM (doorGeom) for the doors view panel layout only.
 * Everything else — brand colours, insert fills, frame swatches — is local to
 * this file so it stays self-contained (the same SVG string this file builds
 * can be re-rendered server-side, e.g. into an email, with zero changes).
 *
 * Vanilla ES2020, no build step. Exposes window.WD_RENDER only.
 */
(function () {
  'use strict';

  /* ---- coordinate maths (plan section 5, verbatim) ---- */
  var VW = 480;
  var VH = 360;
  var PAD = { t: 30, r: 24, b: 44, l: 34 };
  var AVAIL_W = VW - PAD.l - PAD.r;
  var AVAIL_H = VH - PAD.t - PAD.b;

  /* ---- visual constants standing in for the CRM's fixed pixel offsets ---- */
  var TOP_SHELF_DROP_MM = 400; /* CRM used a fixed 18 viewBox units; mm keeps it proportionate */
  var SINGLE_RAIL_DROP_MM = 400; /* rail sits this far below the shelf line, in mm-space */
  var DOUBLE_RAIL_OFFSET_UNITS = 16; /* fixed viewBox-unit offset below each hang-zone top */

  /* ---- Midnight Atelier brand tokens (NOT the plan's Joinery tokens) ---- */
  var BRAND = {
    ink: '#20263A',
    brass: '#B08A4F',
    hairline: '#E2DCCF',
    ivory: '#FBF8F2',
    bone: '#F4EFE6',
    muted: '#8A8778',
    drawerFront: '#F3EDE2',
    mirrorFrom: '#EAEFF3',
    mirrorTo: '#DFE7ED',
    board: '#F3EFE8',
    frosted: '#EDF1F0',
    colour: '#DDE6EA'
  };
  /* Panel fills and frame colours are looked up in WD_CATALOGUE at draw time,
     so adding or renaming a finish never needs a change in here. A stale copy
     of these maps is exactly why new finishes silently drew as the default. */
  function catalogue() {
    return (typeof window !== 'undefined' && window.WD_CATALOGUE) ? window.WD_CATALOGUE : null;
  }
  function insertDef(key) {
    var cat = catalogue();
    if (cat && cat.doors && cat.doors.inserts) {
      for (var i = 0; i < cat.doors.inserts.length; i++) {
        if (cat.doors.inserts[i].value === key) return cat.doors.inserts[i];
      }
    }
    return null;
  }
  function frameColourFor(key) {
    var cat = catalogue();
    if (cat && cat.doors && cat.doors.frames) {
      for (var i = 0; i < cat.doors.frames.length; i++) {
        if (cat.doors.frames[i].value === key) return cat.doors.frames[i].swatch;
      }
    }
    return BRAND.ink;
  }
  /* Dark panels need a light handle to stay visible, light ones a dark handle. */
  function isDark(hex) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
    if (!m) return false;
    var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
  }
  var FONT = 'Jost, sans-serif';

  var SECTION_WORD = { drawer: 'drawers', shelf: 'shelves', single: 'hanging', double: 'hanging' };
  var ARIA_SECTION_WORDS = { drawer: 'drawer tower', shelf: 'shelf tower', single: 'single hanging', double: 'double hanging' };
  var NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

  var uidCounter = 0;
  function nextUid() {
    uidCounter += 1;
    return uidCounter;
  }

  /* ---------------------------------------------------------------------
     Small defensive helpers — nothing here should ever be able to emit
     "NaN" or "undefined" into the returned markup.
  --------------------------------------------------------------------- */
  function f(n) {
    if (typeof n !== 'number' || !isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
  }

  function clampNum(v, fallback, min, max) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    if (typeof n !== 'number' || !isFinite(n)) n = fallback;
    if (min != null && n < min) n = min;
    if (max != null && n > max) n = max;
    return n;
  }

  function clampInt(v, fallback, min, max) {
    return Math.round(clampNum(v, fallback, min, max));
  }

  function escapeXml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function findOpt(list, value) {
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].value === value) return list[i];
    }
    return null;
  }

  function numberWord(n) {
    return (typeof n === 'number' && n >= 0 && n < NUMBER_WORDS.length) ? NUMBER_WORDS[n] : String(n || 0);
  }

  /* ---------------------------------------------------------------------
     SVG element builders — plain strings, no DOM.
  --------------------------------------------------------------------- */
  function line(x1, y1, x2, y2, stroke, width, roundCap) {
    return '<line x1="' + f(x1) + '" y1="' + f(y1) + '" x2="' + f(x2) + '" y2="' + f(y2) + '" stroke="' +
      (stroke || BRAND.ink) + '" stroke-width="' + f(width == null ? 1 : width) + '"' +
      (roundCap ? ' stroke-linecap="round"' : '') + '></line>';
  }

  function streak(x1, y1, x2, y2, width, opacity) {
    return '<line x1="' + f(x1) + '" y1="' + f(y1) + '" x2="' + f(x2) + '" y2="' + f(y2) +
      '" stroke="#FFFFFF" stroke-width="' + f(width) + '" opacity="' + opacity + '"></line>';
  }

  function circle(cx, cy, r, fill) {
    return '<circle cx="' + f(cx) + '" cy="' + f(cy) + '" r="' + f(r) + '" fill="' + (fill || BRAND.brass) + '"></circle>';
  }

  function rect(x, y, width, height, attrs) {
    attrs = attrs || {};
    var out = '<rect x="' + f(x) + '" y="' + f(y) + '" width="' + f(width) + '" height="' + f(height) + '"';
    out += ' fill="' + (attrs.fill || 'none') + '"';
    if (attrs.stroke) out += ' stroke="' + attrs.stroke + '"';
    if (attrs.strokeWidth != null) out += ' stroke-width="' + f(attrs.strokeWidth) + '"';
    if (attrs.rx != null) out += ' rx="' + f(attrs.rx) + '"';
    if (attrs.extra) out += ' ' + attrs.extra;
    return out + '></rect>';
  }

  function text(x, y, str, fill, size) {
    return '<text x="' + f(x) + '" y="' + f(y) + '" text-anchor="middle" font-family="' + FONT +
      '" font-size="' + f(size == null ? 9 : size) + '" fill="' + (fill || BRAND.muted) + '">' +
      escapeXml(str == null ? '' : String(str)) + '</text>';
  }

  function arrowHead(x, y, dir, vertical) {
    var s = 4, t = s / 1.6;
    if (vertical) {
      var y2 = y + dir * s;
      return '<polyline points="' + f(x - t) + ',' + f(y2) + ' ' + f(x) + ',' + f(y) + ' ' + f(x + t) + ',' + f(y2) +
        '" fill="none" stroke="' + BRAND.muted + '" stroke-width="1"></polyline>';
    }
    var x2 = x + dir * s;
    return '<polyline points="' + f(x2) + ',' + f(y - t) + ' ' + f(x) + ',' + f(y) + ' ' + f(x2) + ',' + f(y + t) +
      '" fill="none" stroke="' + BRAND.muted + '" stroke-width="1"></polyline>';
  }

  function carcassFillRef(defs, uid) {
    var id = 'wd-carcass-' + uid;
    defs.push('<linearGradient id="' + id + '" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="' + BRAND.ivory + '"></stop>' +
      '<stop offset="1" stop-color="' + BRAND.bone + '"></stop></linearGradient>');
    return 'url(#' + id + ')';
  }

  function mirrorFillRef(defs, uid, fromColour) {
    var id = 'wd-mirror-' + uid;
    var from = fromColour || BRAND.mirrorFrom;
    defs.push('<linearGradient id="' + id + '" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="' + from + '"></stop>' +
      '<stop offset="1" stop-color="' + shade(from, -0.06) + '"></stop></linearGradient>');
    return 'url(#' + id + ')';
  }
  /* Nudge a hex toward black (negative) or white (positive). */
  function shade(hex, amt) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
    if (!m) return hex;
    function ch(v) {
      var n = parseInt(v, 16) + Math.round(255 * amt);
      return ('0' + Math.max(0, Math.min(255, n)).toString(16)).slice(-2);
    }
    return '#' + ch(m[1]) + ch(m[2]) + ch(m[3]);
  }

  /* ---------------------------------------------------------------------
     Frame: scale + origin for a given opening size, plus mm-space helpers.
  --------------------------------------------------------------------- */
  function makeFrame(W, H) {
    var sc = Math.min(AVAIL_W / W, AVAIL_H / H);
    if (!isFinite(sc) || sc <= 0) sc = 0.01;
    var w = W * sc;
    var h = H * sc;
    var x0 = PAD.l + (AVAIL_W - w) / 2;
    var y0 = PAD.t + (AVAIL_H - h) / 2;
    return {
      sc: sc,
      w: w,
      h: h,
      x0: x0,
      y0: y0,
      bottom: y0 + h,
      mx: function (mm) { return x0 + mm * sc; },
      my: function (mm) { return y0 + mm * sc; }
    };
  }

  /* ---------------------------------------------------------------------
     Internals view — top shelf, dividers, and per-type proportions ported
     from drawElev(): drawer tower, shelf tower, single hang, double hang.
  --------------------------------------------------------------------- */
  function renderSection(body, sec, cxMm, swMm, topYmm, H, frame) {
    var type = sec.t;
    var x0px = frame.mx(cxMm);
    var swPx = swMm * frame.sc;
    var bottomPx = frame.bottom;

    if (type === 'drawer') {
      var hangAbove = sec.hangAbove !== false;
      var zoneHeightMm = H - topYmm;
      var dTopMm = topYmm + (hangAbove ? zoneHeightMm * 0.42 : 0);
      var dTopPx = frame.my(dTopMm);

      if (hangAbove) {
        var hangHeightMm = dTopMm - topYmm;
        var ryPx = frame.my(topYmm + hangHeightMm * 0.26);
        body.push(line(x0px + 5, ryPx, x0px + swPx - 5, ryPx, BRAND.brass, 1.4, true));
        body.push(circle(x0px + swPx / 2, ryPx, 2.6, BRAND.brass));
        var tickBottomPx = ryPx + (bottomPx - ryPx) * 0.18;
        for (var k = 1; k <= 3; k++) {
          var hx = x0px + swPx * (k / 4);
          body.push(line(hx, ryPx, hx, tickBottomPx, BRAND.hairline, 1));
        }
        body.push(line(x0px, dTopPx, x0px + swPx, dTopPx, BRAND.ink, 1));
      }

      var drawers = clampInt(sec.drawers, 4, 1, 20);
      var dhPx = (bottomPx - dTopPx) / drawers;
      for (var k1 = 1; k1 < drawers; k1++) {
        var dy = dTopPx + dhPx * k1;
        body.push(line(x0px + 3, dy, x0px + swPx - 3, dy, BRAND.ink, 1));
      }
      for (var k2 = 0; k2 < drawers; k2++) {
        var hy = dTopPx + dhPx * k2 + dhPx / 2;
        body.push(rect(x0px + swPx / 2 - 8, hy - 1.2, 16, 2.4, { fill: BRAND.brass, rx: 1.2 }));
      }
    } else if (type === 'shelf') {
      var shelves = clampInt(sec.shelves, 5, 1, 20);
      var topPx = frame.my(topYmm);
      var shPx = (bottomPx - topPx) / (shelves + 1);
      for (var k3 = 1; k3 <= shelves; k3++) {
        var sy = topPx + shPx * k3;
        body.push(line(x0px + 3, sy, x0px + swPx - 3, sy, BRAND.ink, 1));
      }
    } else if (type === 'single') {
      var railPx = frame.my(topYmm + SINGLE_RAIL_DROP_MM);
      body.push(line(x0px + 5, railPx, x0px + swPx - 5, railPx, BRAND.brass, 1.4, true));
      body.push(circle(x0px + swPx / 2, railPx, 3, BRAND.brass));
      var dropPx = (bottomPx - railPx) * 0.62;
      for (var k4 = 1; k4 <= 4; k4++) {
        var hx4 = x0px + swPx * (k4 / 5);
        body.push(line(hx4, railPx, hx4, railPx + dropPx, BRAND.hairline, 1));
      }
    } else if (type === 'double') {
      var zoneTopPx = frame.my(topYmm);
      var midPx = zoneTopPx + (bottomPx - zoneTopPx) / 2;
      body.push(line(x0px, midPx, x0px + swPx, midPx, BRAND.ink, 1));
      var halfPx = midPx - zoneTopPx;
      var dropPxD = halfPx * 0.55;
      [zoneTopPx, midPx].forEach(function (zTop) {
        var ryPxD = zTop + DOUBLE_RAIL_OFFSET_UNITS;
        body.push(line(x0px + 5, ryPxD, x0px + swPx - 5, ryPxD, BRAND.brass, 1.4, true));
        body.push(circle(x0px + swPx / 2, ryPxD, 3, BRAND.brass));
        for (var k5 = 1; k5 <= 4; k5++) {
          var hx5 = x0px + swPx * (k5 / 5);
          body.push(line(hx5, ryPxD, hx5, ryPxD + dropPxD, BRAND.hairline, 1));
        }
      });
    }
  }

  function renderInternals(body, state, W, H, frame, thumbnail, selected) {
    var internals = state.internals || {};
    var sections = Array.isArray(internals.sections) ? internals.sections : [];
    var topShelf = internals.topShelf !== false;
    var topYmm = topShelf ? Math.min(TOP_SHELF_DROP_MM, H) : 0;

    if (topShelf) {
      body.push(line(frame.x0, frame.my(topYmm), frame.x0 + frame.w, frame.my(topYmm), BRAND.ink, 1));
    }

    var cxMm = 0;
    sections.forEach(function (rawSec, i) {
      var sec = rawSec || {};
      var swMm = clampNum(sec.w, 0, 0, 100000);
      var x0px = frame.mx(cxMm);
      var swPx = swMm * frame.sc;

      if (i > 0) {
        body.push(line(x0px, frame.my(topYmm), x0px, frame.bottom, BRAND.ink, 1));
      }

      renderSection(body, sec, cxMm, swMm, topYmm, H, frame);

      body.push('<rect data-sec="' + i + '" x="' + f(x0px) + '" y="' + f(frame.y0) + '" width="' + f(swPx) +
        '" height="' + f(frame.h) + '" fill="transparent" style="pointer-events:all"></rect>');

      if (selected === i) {
        body.push('<rect x="' + f(x0px) + '" y="' + f(frame.y0) + '" width="' + f(swPx) + '" height="' + f(frame.h) +
          '" fill="none" stroke="' + BRAND.brass + '" stroke-width="2"></rect>');
      }

      if (!thumbnail && swPx > 20) {
        body.push(text(x0px + swPx / 2, frame.bottom - 8, SECTION_WORD[sec.t] || '', BRAND.muted, 8.5));
      }

      cxMm += swMm;
    });
  }

  /* ---------------------------------------------------------------------
     Doors view — n equal panels from WD_GEOM.doorGeom, insert-specific
     fills, a vertical pull handle per panel, alternating 2-unit y offset
     for sliding so the two tracks read.
  --------------------------------------------------------------------- */
  function renderDoors(body, defs, uid, state, W, H, frame, thumbnail) {
    var doors = state.doors || {};
    var type = doors.type || 'sliding';
    if (type === 'none') return;

    var geom = (typeof window !== 'undefined' && window.WD_GEOM && typeof window.WD_GEOM.doorGeom === 'function')
      ? window.WD_GEOM.doorGeom(W, H, type, doors.count)
      : null;
    var n = geom && geom.n ? geom.n : 2;
    if (!isFinite(n) || n < 1) n = 2;
    var pw = frame.w / n;

    var insertKey = doors.insert || 'mirror';
    var frameKey = doors.frame || 'matte-black';
    var frameColour = frameColourFor(frameKey);
    var ins = insertDef(insertKey);
    var baseFill = ins && ins.fill ? ins.fill : BRAND.board;
    var wantStreak = !!(ins && ins.streak);
    var fillRef = wantStreak ? mirrorFillRef(defs, uid, baseFill) : baseFill;
    var handleColour = isDark(baseFill) ? 'rgba(255,255,255,0.72)' : BRAND.muted;

    for (var k = 0; k < n; k++) {
      var px = frame.x0 + pw * k;
      var yOff = (type === 'sliding' && k % 2 === 1) ? 2 : 0;
      var panelY = frame.y0 + 2 + yOff;
      var panelH = frame.h - 4;

      body.push(rect(px + 1.5, panelY, pw - 3, panelH, { fill: fillRef, stroke: frameColour, strokeWidth: 1.4 }));

      if (wantStreak) {
        body.push(streak(px + 6, panelY + panelH - 14, px + pw - 14, panelY + 10, 7, 0.85));
        body.push(streak(px + 16, panelY + panelH - 8, px + pw - 6, panelY + 22, 3, 0.7));
      }

      if (!thumbnail) {
        body.push(rect(px + pw - 11, frame.y0 + frame.h / 2 - 14 + yOff, 3.5, 28, { fill: handleColour, rx: 1.8 }));
      }
    }
  }

  /* ---------------------------------------------------------------------
     Overall dimensions — one width line, one height line, arrowheads, mm
     labels. Never rendered per-section; never a warning.
  --------------------------------------------------------------------- */
  function renderDims(body, W, H, frame) {
    var dy = frame.bottom + 14;
    body.push(line(frame.x0, dy, frame.x0 + frame.w, dy, BRAND.muted, 1));
    body.push(arrowHead(frame.x0, dy, 1, false));
    body.push(arrowHead(frame.x0 + frame.w, dy, -1, false));
    body.push(text(frame.x0 + frame.w / 2, dy + 12, Math.round(W) + 'mm', BRAND.muted, 9));

    var dx = frame.x0 - 14;
    body.push(line(dx, frame.y0, dx, frame.bottom, BRAND.muted, 1));
    body.push(arrowHead(dx, frame.y0, 1, true));
    body.push(arrowHead(dx, frame.bottom, -1, true));
    var lx = dx - 10, ly = frame.y0 + frame.h / 2;
    body.push('<text x="' + f(lx) + '" y="' + f(ly) + '" text-anchor="middle" font-family="' + FONT +
      '" font-size="9" fill="' + BRAND.muted + '" transform="rotate(-90 ' + f(lx) + ' ' + f(ly) + ')">' +
      Math.round(H) + 'mm</text>');
  }

  /* ---------------------------------------------------------------------
     renderElevation(state, view, opts) -> SVG string
  --------------------------------------------------------------------- */
  function renderElevation(state, view, opts) {
    opts = opts || {};
    state = state || {};

    var opening = state.opening || {};
    var W = clampNum(opening.w, 2400, 100, 20000);
    var H = clampNum(opening.h, 2100, 100, 20000);
    var thumbnail = !!opts.thumbnail;
    var showDims = thumbnail ? false : opts.showDims !== false;
    var selected = typeof opts.selected === 'number' ? opts.selected : -1;

    var frame = makeFrame(W, H);
    var uid = nextUid();
    var defs = [];
    var body = [];

    body.push(rect(frame.x0, frame.y0, frame.w, frame.h, {
      fill: carcassFillRef(defs, uid),
      stroke: BRAND.ink,
      strokeWidth: 1.4
    }));

    if (view === 'door') {
      renderDoors(body, defs, uid, state, W, H, frame, thumbnail);
    } else {
      renderInternals(body, state, W, H, frame, thumbnail, selected);
    }

    if (showDims) {
      renderDims(body, W, H, frame);
    }

    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + VW + ' ' + VH +
      '" width="100%" height="100%" role="img" preserveAspectRatio="xMidYMid meet">' +
      (defs.length ? '<defs>' + defs.join('') + '</defs>' : '') +
      body.join('') +
      '</svg>';
  }

  /* ---------------------------------------------------------------------
     ariaLabel(state, cat) -> "Wardrobe elevation, 2400 by 2100 millimetres,
     drawer tower, double hanging, drawer tower, three mirror sliding doors."
  --------------------------------------------------------------------- */
  function ariaLabel(state, cat) {
    state = state || {};
    var opening = state.opening || {};
    var W = opening.w;
    var H = opening.h;
    var bits = ['Wardrobe elevation'];

    if (typeof W === 'number' && typeof H === 'number' && isFinite(W) && isFinite(H)) {
      bits.push(W + ' by ' + H + ' millimetres');
    }

    var sections = (state.internals && state.internals.sections) || [];
    sections.forEach(function (s) {
      if (s && s.t) bits.push(ARIA_SECTION_WORDS[s.t] || String(s.t));
    });

    var doors = state.doors || {};
    if (doors.type && doors.type !== 'none') {
      var geo = (typeof window !== 'undefined' && window.WD_GEOM && typeof window.WD_GEOM.doorGeom === 'function')
        ? window.WD_GEOM.doorGeom(W, H, doors.type, doors.count)
        : null;
      var n = geo && geo.n ? geo.n : 0;
      var doorTypes = cat && cat.doors && cat.doors.types;
      var inserts = cat && cat.doors && cat.doors.inserts;
      var typeOpt = findOpt(doorTypes, doors.type);
      var insertOpt = findOpt(inserts, doors.insert);
      var typeLabel = (typeOpt && typeOpt.label) ? typeOpt.label : String(doors.type);
      var insertLabel = (insertOpt && insertOpt.label) ? insertOpt.label : String(doors.insert || '');
      bits.push((numberWord(n) + ' ' + insertLabel.toLowerCase() + ' ' + typeLabel.toLowerCase() + ' doors')
        .replace(/\s+/g, ' ').trim());
    } else {
      bits.push('no doors');
    }

    return bits.join(', ') + '.';
  }

  /* Where the carcass actually sits inside the 480x360 viewBox, as
     percentages. The SVG letterboxes the robe, so the 3D side faces must be
     positioned from this rather than from the frame element's own box. */
  function carcassBox(W, H) {
    var f = makeFrame(clampNum(W, 2400, 100, 20000), clampNum(H, 2100, 100, 20000));
    return {
      leftPct: (f.x0 / VW) * 100,
      rightPct: ((VW - (f.x0 + f.w)) / VW) * 100,
      topPct: (f.y0 / VH) * 100,
      bottomPct: ((VH - (f.y0 + f.h)) / VH) * 100
    };
  }

  window.WD_RENDER = {
    renderElevation: renderElevation,
    ariaLabel: ariaLabel,
    carcassBox: carcassBox
  };
})();
